import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";

// Slice del flujo asíncrono de exportación de arqueos. Sustituye al
// `POST /data/downloadArqueos` síncrono (bloqueaba el proceso backend
// para rangos amplios). Ahora:
//   1. Se crea un job → estado PENDING → se pinta en la tabla.
//   2. El backend lo procesa en background → RUNNING → COMPLETED.
//   3. El polling refresca la tabla cada 3s mientras haya jobs vivos.
//   4. Botón "Descargar" cuando llega a COMPLETED.

const initialState = {
  jobs: [],
  isLoading: false,
  isCreating: false,
  error: null,
};

const slice = createSlice({
  name: "arqueoExports",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    startCreating(state) {
      state.isCreating = true;
      state.error = null;
    },
    stopCreating(state) {
      state.isCreating = false;
    },
    setJobs(state, action) {
      state.jobs = action.payload || [];
      state.isLoading = false;
    },
    upsertJob(state, action) {
      const j = action.payload;
      if (!j) return;
      const idx = state.jobs.findIndex((x) => x.id === j.id);
      if (idx >= 0) state.jobs[idx] = j;
      else state.jobs.unshift(j);
    },
    hasError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
      state.isCreating = false;
    },
  },
});

export default slice.reducer;

export const {
  startLoading,
  stopLoading,
  startCreating,
  stopCreating,
  setJobs,
  upsertJob,
  hasError,
} = slice.actions;

// ─── Selectors ────────────────────────────────────────────────────────

export const selectHasLiveJobs = (state) =>
  (state.arqueoExports.jobs || []).some(
    (j) => j.status === "PENDING" || j.status === "RUNNING",
  );

// ─── Thunks ───────────────────────────────────────────────────────────

export function fetchRecentExports(take = 20) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const r = await axios.get(`/data/arqueos/exports/recent?take=${take}`);
      dispatch(setJobs(r.data || []));
    } catch (e) {
      dispatch(hasError(e?.message ?? "error"));
    }
  };
}

export function createExport({
  start,
  end,
  sendEmail = false,
  extraEmail = "",
}) {
  return async (dispatch) => {
    dispatch(startCreating());
    try {
      const toIsoDate = (d) =>
        typeof d === "string"
          ? d
          : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const r = await axios.post("/data/arqueos/exports", {
        Start: toIsoDate(start),
        End: toIsoDate(end),
        SendEmail: sendEmail,
        ExtraEmail: extraEmail,
      });
      dispatch(upsertJob(r.data));
      dispatch(stopCreating());
      return r.data;
    } catch (e) {
      dispatch(hasError(e?.message ?? "error"));
      throw e;
    }
  };
}
